import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { actions, ACTIONS_TYPES } from 'redux/local';
import Shared from 'screens/shared';
import { Paper } from '@mui/material';
import CreateForm from 'screens/monitoring/alarms-list/create';
import { Redirect } from 'react-router-dom';
import {
  ROUTE,
  TITLE_MONITORING_ALARM_EXPRESSION_COMPARISON_OPERATOR,
  TITLE_MONITORING_ALARM_EXPRESSION_METRIC,
  TITLE_MONITORING_ALARM_EXPRESSION_RANGEAGGREGATION,
  TITLE_MONITORING_ALARM_EXPRESSION_UNIT,
} from 'constant';
import PropertiesTable from 'screens/shared/properties-table';
import {
  createLink, isEmptyArray, isEmptyObject, uppercase,
} from 'utils';
import { MonitoringAlarm } from 'models';
import Transitions from './transitions';

export const MonitoringAlarmDetails = ({ match = {}, history }) => {
  const { params = {} } = match;
  const { id: alarmId = '' } = params;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  const {
    [ACTIONS_TYPES.MONITORING.ALARM.GET]: _monitoringAlarm = {},
    [ACTIONS_TYPES.SERVER.GET_OVERALL_SERVERS]: servers = [],
    [ACTIONS_TYPES.MONITORING.ACTION.GET_ALL]: monitoringActions = [],
  } = useSelector((e) => e);

  const [, successes] = Shared.hooks.useNotification({
    actions: [ACTIONS_TYPES.MONITORING.ALARM.DELETE],
  });

  const monitoringAlarm = new MonitoringAlarm(_monitoringAlarm);

  const getServerUuid = () => {
    const { expression = {} } = monitoringAlarm.getProperties();
    const { source = {} } = expression;
    const { uuid: serverUuid = '' } = source;
    return serverUuid;
  };

  const getMonitoringActionsName = () => {
    if (!monitoringActions.length) return '';
    const { firingActions = [] } = monitoringAlarm.getProperties();
    const filteredActions = firingActions.map((actionId) => monitoringActions.find((e) => e.id === actionId));
    return filteredActions.map(({ id: actionId, properties }) => (
      <span
        key={actionId}
        className="chip clickable"
        onClick={() => history.push(ROUTE.DASHBOARD.MONITORING_ACTION.path.replace(':id', actionId))}
      >
        {properties.name}
      </span>
    ));
  };

  const sourceServer = servers.find((e) => e.id === getServerUuid(monitoringAlarm)) || {};
  const { properties: serverProps = {} } = sourceServer;
  const { name: serverName = '' } = serverProps;
  const monitoringActionsName = getMonitoringActionsName();

  useEffect(() => {
    if (alarmId) actions.monitoring.alarm.get({ id: alarmId });
  }, [alarmId]);

  useEffect(() => {
    if (isEmptyArray(servers)) actions.server.getOverallServers(true);
    if (isEmptyArray(monitoringActions)) actions.monitoring.action.getAll(true);
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    actions.common.setLayoutTitle({
      layout: { title: 'Monitoring Alarm', menu: ROUTE.DASHBOARD.MONITORING.key },
    });
  }, []);

  const getPropertiesCells = () => {
    const {
      expression = {}, alarmDelay = '',
    } = monitoringAlarm.getProperties();

    const {
      metric = '',
      unit = '',
      comparisonOperator = '',
      comparisonThreshold = '',
      range = '',
      rangeAggregation = '',
    } = expression;

    return [
      ['Metric', TITLE_MONITORING_ALARM_EXPRESSION_METRIC[uppercase(metric)]],
      ['Unit', TITLE_MONITORING_ALARM_EXPRESSION_UNIT[unit]],
      ['Threshold Type', TITLE_MONITORING_ALARM_EXPRESSION_COMPARISON_OPERATOR[comparisonOperator]],
      ['Threshold', comparisonThreshold],
      ['Range', range],
      ['Range Aggregation', TITLE_MONITORING_ALARM_EXPRESSION_RANGEAGGREGATION[rangeAggregation]],
      ['Alarm Delay', alarmDelay],
      ['Actions', monitoringActionsName],
    ];
  };

  const onDelete = (isDelete) => {
    setShowConfirmDialog(false);
    if (isDelete) {
      actions.monitoring.alarm.delete({ id: alarmId });
    }
  };

  const serverClickHandler = () => {
    const { id: serverId = '', dataCenter = {} } = sourceServer;
    const { id: dcId = '' } = dataCenter;
    history.push(createLink(ROUTE.DASHBOARD.SERVER, { dcId, id: serverId }));
  };

  const monitorClickHandler = () => {
    const { id: serverId = '', dataCenter = {} } = sourceServer;
    const { id: dcId = '' } = dataCenter;
    history.push(createLink(ROUTE.DASHBOARD.MONITORING_SERVER, { dcId, serverId }));
  };

  return (
    <div className="screen-server">
      <div className="strap">
        <div className="strap-details">
          <Shared.widgets.Icon name="bell" style={{ fontSize: 30 }} />
          <div style={{ marginLeft: 15 }}>
            <span style={{ display: 'block', marginLeft: 0 }} className="strap-details-title">
              {monitoringAlarm.getProperty('name')}
            </span>

            {!isEmptyObject(sourceServer) && (
              <span
                style={{
                  display: 'flex', justifyItems: 'center', marginTop: 4,
                }}
              >
                <span className="link" onClick={serverClickHandler}>
                  <Shared.widgets.Icon name="server" style={{ fontSize: 14 }} />
                  <span style={{ marginLeft: 5, fontSize: 12 }}>
                    {serverName}
                  </span>
                </span>
                <span style={{ margin: '0 10px', color: 'gray' }}>-</span>
                <span className="link" onClick={monitorClickHandler}>
                  <Shared.widgets.Icon name="chart-line" style={{ fontSize: 14 }} />
                  <span style={{ marginLeft: 5, fontSize: 12 }}>
                  Monitor
                  </span>
                </span>
              </span>
            )}
          </div>
        </div>
        <div className="strap-details-icons">
          <Shared.widgets.Icon
            name="trash"
            className="danger-txt"
            onClick={() => setShowConfirmDialog(true)}
          />
          <Shared.widgets.Icon
            name="edit"
            onClick={() => setIsUpdate(!isUpdate)}
          />
        </div>
      </div>
      {isUpdate && (
        <Paper>
          <CreateForm
            monitoringAlarm={monitoringAlarm}
            onCancel={() => setIsUpdate(false)}
          />
        </Paper>
      )}
      <PropertiesTable properties={getPropertiesCells()} />
      <Transitions alarmId={alarmId} />
      <Shared.dialog.Confirm show={showConfirmDialog} onClose={onDelete} />
      {!!successes.length && <Redirect to={ROUTE.DASHBOARD.MONITORING_ALARMS.path} />}
    </div>
  );
};
